import { Button, Tooltip, useNotification } from '@eye-able/ui';
import { Copy } from 'lucide-react';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export interface CopyToClipboardProps {
  text: string;
  /** Accessible label + tooltip; defaults to a generic "copy". */
  label?: string;
  className?: string;
  /** Icon button size (defaults to `icon`). */
  size?: 'icon' | 'icon-xs';
}

const CopyToClipboard = ({
  text,
  label,
  className,
  size = 'icon',
}: CopyToClipboardProps) => {
  const { t } = useInlineTranslation();
  const copyLabel = label ?? t('copy');

  const handleCopy = useCopyToClipboard();
  const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard;

  return (
    <Tooltip content={copyLabel}>
      {/* Wrapper keeps the tooltip usable while the button is disabled. */}
      <span className='eye:inline-flex'>
        <Button
          type='button'
          size={size}
          variant='ghost'
          aria-label={copyLabel}
          disabled={!canCopy}
          onClick={() => void handleCopy(text)}
          className={className}
        >
          <Copy aria-hidden />
        </Button>
      </span>
    </Tooltip>
  );
};

export function useCopyToClipboard() {
  const notification = useNotification();
  const { t } = useInlineTranslation();

  const copyToClipboard = async (text: string) => {
    if (!navigator.clipboard?.writeText) {
      notification.error(t('textCopyError'));
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      notification.success(t('textCopied'));
    } catch {
      notification.error(t('textCopyError'));
    }
  };

  return copyToClipboard;
}
export default CopyToClipboard;
